import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { SparkleIcon, ArrowLeft } from '../components/ui/Icons';
import { Eye, EyeOff, Check } from 'lucide-react';

export default function RegisterPage() {
    const navigate = useNavigate();
    const { register, login } = useAuth();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const passwordRequirements = [
        { met: password.length >= 8, text: 'Alespoň 8 znaků' },
        { met: /[A-Z]/.test(password), text: 'Jedno velké písmeno' },
        { met: /[0-9]/.test(password), text: 'Jedna číslice' },
    ];

    const isPasswordValid = passwordRequirements.every(r => r.met);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isPasswordValid) {
            toast.error('Heslo nesplňuje požadavky');
            return;
        }

        setLoading(true);

        try {
            await register(name, email, password);
            toast.success('Registrace úspěšná!');
            await login(email, password);
            navigate('/');
        } catch (error) {
            const message = error.response?.data?.detail || 'Registrace se nezdařila';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" data-testid="register-page">
            {/* Sparkle */}
            <SparkleIcon className="w-6 h-10 text-foreground/60 mb-8" />
            
            {/* Header */}
            <h1 className="text-3xl md:text-4xl italic mb-2">
                Vytvořte si účet
            </h1>
            <p className="text-muted-foreground italic mb-4">
                Začněte číst povídky v Listovně
            </p>

            {/* Trial badge */}
            <div className="border border-foreground/20 px-6 py-3 mb-12 text-center">
                <p className="italic">
                    První měsíc zdarma! Poté 149 Kč/měsíc.
                </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
                <div className="form-field">
                    <label htmlFor="name" className="italic">Jméno</label>
                    <Input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Vaše jméno"
                        required
                        className="bg-transparent border-0 border-b border-foreground/30 rounded-none focus:ring-0 focus:border-foreground px-0"
                        data-testid="register-name"
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="email" className="italic">E-mail</label>
                    <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="vas@email.cz"
                        required
                        className="bg-transparent border-0 border-b border-foreground/30 rounded-none focus:ring-0 focus:border-foreground px-0"
                        data-testid="register-email"
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="password" className="italic">Heslo</label>
                    <div className="relative">
                        <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            className="bg-transparent border-0 border-b border-foreground/30 rounded-none focus:ring-0 focus:border-foreground px-0 pr-10"
                            data-testid="register-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    
                    {/* Password requirements */}
                    {password && (
                        <div className="mt-4 space-y-2">
                            {passwordRequirements.map((req, i) => (
                                <div 
                                    key={i} 
                                    className={`flex items-center gap-2 text-sm ${
                                        req.met ? 'text-foreground' : 'text-muted-foreground'
                                    }`}
                                >
                                    <Check className={`w-3 h-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                                    <span className="italic">{req.text}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Button 
                    type="submit" 
                    variant="outline"
                    className="w-full italic mt-8"
                    disabled={loading || !isPasswordValid}
                    data-testid="register-submit"
                >
                    {loading ? 'Registrace...' : 'Vytvořit účet'}
                </Button>
            </form>

            {/* Login link */}
            <p className="text-center mt-8 text-muted-foreground">
                Už máte účet?{' '}
                <Link 
                    to="/prihlaseni" 
                    className="italic hover:underline underline-offset-4"
                    data-testid="login-link"
                >
                    Přihlaste se
                </Link>
            </p>

            {/* Back link */}
            <div className="mt-12 flex items-center gap-2">
                <ArrowLeft className="w-8 h-4 text-foreground/60" />
                <Link 
                    to="/" 
                    className="italic hover:underline underline-offset-4"
                >
                    zpět na homepage
                </Link>
            </div>
        </div>
    );
}
